import { fmtDistance, fmtDuration, fmtDurationShort, fmtSpeed } from "./format";
import { formatActivityDate, formatActivityLocalTime, getActivityLocalDateKey } from "./dateTime";
import { deriveActivityName } from "./activityName";
import { resolveElapsedSeconds } from "./motionMetrics";

const LABELS = {
  duration_seconds: "Duration",
  distance_m: "Distance",
  avg_heart_rate: "Heart rate",
  avg_power: "Power",
  avg_cadence: "Cadence",
  avg_speed_ms: "Speed",
  avg_pace_s_per_km: "Pace",
};

export function comparisonMetricLabel(key) {
  return LABELS[key] || key;
}

function paceLabel(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return "";
  const total = Math.round(Number(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}/km`;
}

export function formatComparisonMetric(key, value, { sport, unitSystem } = {}) {
  if (value == null || value === "") return "";
  if (key === "duration_seconds") return fmtDuration(value);
  if (key === "distance_m") return fmtDistance(value, unitSystem);
  if (key === "avg_heart_rate") return `${Math.round(value)} bpm`;
  if (key === "avg_power") return `${Math.round(value)} W`;
  if (key === "avg_cadence") return `${Math.round(value)}`;
  if (key === "avg_speed_ms") return fmtSpeed(value, sport || "cycling", unitSystem);
  if (key === "avg_pace_s_per_km") return paceLabel(value);
  return String(value);
}

export function compareCandidateDurationLabel(activity) {
  const seconds = resolveElapsedSeconds(activity);
  if (seconds == null || !Number.isFinite(Number(seconds)) || Number(seconds) <= 0) {
    return "";
  }
  const label = fmtDurationShort(seconds);
  return !label || label === "—" ? "" : label;
}

export function compareCandidateDisplay(activity) {
  const date = formatActivityDate(activity);
  const time = formatActivityLocalTime(activity);
  const duration = compareCandidateDurationLabel(activity);
  const parts = [];
  if (date && date !== "—") parts.push(date);
  if (time) parts.push(time);
  if (duration) parts.push(duration);
  return {
    name: deriveActivityName(activity),
    date,
    when: parts.join(" · ") || "—",
  };
}

export function sameSportCandidates(activities, current) {
  if (!current) return [];
  const sport = current.sport;
  const currentId = current.id || current.activity_id;
  return (activities || [])
    .filter((row) => {
      const id = row.id || row.activity_id;
      return id && id !== currentId && row.sport === sport;
    })
    .sort((left, right) => {
      const dateLeft = getActivityLocalDateKey(left) || "";
      const dateRight = getActivityLocalDateKey(right) || "";
      if (dateLeft !== dateRight) return dateRight.localeCompare(dateLeft);
      const nameLeft = deriveActivityName(left);
      const nameRight = deriveActivityName(right);
      return nameLeft.localeCompare(nameRight);
    });
}

export function intervalEvidenceCopy(intervals) {
  const source = intervals?.interval_source;
  if (source === "recorded_laps") return "From laps in the recording";
  if (source === "auto_detected") return "Detected from samples (fallback)";
  if (source === "summary_only") return "Summary only";
  if (source === "unsupported") return "Not used for this sport";
  if (source === "no_repeated_efforts") return "No repeated efforts found";
  return "";
}
